// Phase 10 — Messages Inbox + DM Threads
// Phase 16 — Avatars across inbox/thread/compose + Online Users sidebar
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  MessageSquare, Send, ArrowLeft, Search, X, Loader2, AlertCircle, Flag,
  Wifi,
} from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ROLE_BADGE = {
  master_admin: "bg-lifted-green/15 text-lifted-green border-lifted-green/40",
  admin:        "bg-lifted-green/15 text-lifted-green border-lifted-green/40",
  affiliate:    "bg-purple-500/15 text-purple-300 border-purple-500/40",
  driver:       "bg-amber-400/15 text-amber-300 border-amber-400/40",
  partner:      "bg-sky-500/15 text-sky-300 border-sky-500/40",
  franchisee:   "bg-rose-500/15 text-rose-300 border-rose-500/40",
  customer:     "bg-white/10 text-white/70 border-white/20",
};

const ROLE_LABEL = {
  affiliate:   "Online Affiliates",
  driver:      "Online Drivers",
  partner:     "Online Partners",
  franchisee:  "Online Franchisees",
  customer:    "Online Customers",
  admin:       "Online Admins",
  master_admin:"Online Admins",
};

function Avatar({ name, avatarUrl, role, size = 36, online }) {
  // Phase 16 — avatar with role-tinted ring + online dot. Falls back to
  // initials when no image is set so we never show a broken image icon.
  const initial = (name || "U").trim().charAt(0).toUpperCase();
  const ringClass = ({
    master_admin: "ring-lifted-green/60",
    admin:        "ring-lifted-green/60",
    affiliate:    "ring-purple-400/50",
    driver:       "ring-amber-400/50",
    partner:      "ring-sky-400/50",
    franchisee:   "ring-rose-400/50",
    customer:     "ring-white/30",
  })[role] || "ring-white/30";
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      {avatarUrl ? (
        <img src={avatarUrl} alt=""
             className={`rounded-full object-cover ring-2 ${ringClass}`}
             style={{ width: size, height: size }}
             onError={(e) => { e.currentTarget.style.display = "none"; }} />
      ) : (
        <div className={`rounded-full bg-black/40 ring-2 ${ringClass} flex items-center justify-center font-anton text-lifted-green`}
             style={{ width: size, height: size, fontSize: size / 2.2 }}>
          {initial}
        </div>
      )}
      {online && (
        <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-lifted-green ring-2 ring-black"
              aria-label="online" />
      )}
    </div>
  );
}

function RoleChip({ role }) {
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full border text-[0.55rem] font-bebas tracking-[0.18em] uppercase ${ROLE_BADGE[role] || ROLE_BADGE.customer}`}
          data-testid={`msg-role-${role}`}>
      {role}
    </span>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return "now";
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h`;
  return `${Math.floor(ms / 86_400_000)}d`;
}

export default function Messages() {
  const { conversationId } = useParams();
  const [convos, setConvos] = useState(null);
  const [online, setOnline] = useState(null);
  const [err, setErr] = useState("");
  const [composeOpen, setComposeOpen] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/messages/conversations");
      setConvos(data.conversations || []);
    } catch (e) { setErr(apiErrorMessage(e)); }
  }, []);

  // Phase 16 — poll online users every 60 seconds so the sidebar
  // reflects active presence in near-real-time.
  const loadOnline = useCallback(async () => {
    try {
      const { data } = await api.get("/messages/online-users");
      setOnline(data);
    } catch (_e) { /* non-fatal */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    loadOnline();
    const t = setInterval(loadOnline, 60_000);
    return () => clearInterval(t);
  }, [loadOnline]);

  // Phase 17 — react to SSE new-message events for instant inbox refresh.
  useEffect(() => {
    const onNew = () => { load(); };
    window.addEventListener("lifted:new-message", onNew);
    return () => window.removeEventListener("lifted:new-message", onNew);
  }, [load]);

  const startConvoWith = async (uid) => {
    try {
      const { data } = await api.post("/messages/conversations", { recipient_user_id: uid });
      load(); navigate(`/messages/${data.conversation.id}`);
    } catch (e) { setErr(apiErrorMessage(e)); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6" data-testid="messages-page">
      <header className="flex items-end justify-between mb-4">
        <div>
          <div className="eyebrow">Network</div>
          <h1 className="font-anton text-3xl uppercase">Messages</h1>
        </div>
        <button onClick={() => setComposeOpen(true)} className="btn-lifted text-xs" data-testid="msg-new-conversation">
          + New conversation
        </button>
      </header>
      {err && <div className="text-rose-300 text-sm mb-3" data-testid="msg-error">{err}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr_260px] gap-4">
        {/* Inbox */}
        <aside className="glass-panel overflow-hidden" data-testid="msg-inbox">
          <div className="px-3 py-2 text-[0.65rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase border-b border-white/5">
            Inbox · {convos?.length ?? 0}
          </div>
          {!convos ? (
            <div className="p-4 text-xs text-lifted-dim">Loading…</div>
          ) : convos.length === 0 ? (
            <div className="p-4 text-xs text-lifted-dim">No conversations yet. Start one.</div>
          ) : (
            <ul className="divide-y divide-white/5 max-h-[70vh] overflow-y-auto">
              {convos.map((c) => (
                <li key={c.id}>
                  <Link to={`/messages/${c.id}`}
                        className={`block px-3 py-2.5 hover:bg-white/5 transition-colors ${conversationId === c.id ? "bg-lifted-green/5 border-l-2 border-lifted-green" : ""}`}
                        data-testid={`msg-convo-${c.id}`}>
                    <div className="flex items-center gap-2">
                      <Avatar name={c.other?.name} avatarUrl={c.other?.avatar_url}
                              role={c.other?.role} size={36}
                              online={c.other?.online_status === "online"} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-bebas tracking-wide text-sm text-white truncate">
                            {c.other?.name || "Unknown"}
                          </div>
                          <span className="text-[0.6rem] text-lifted-dim shrink-0">{timeAgo(c.last_message_at)}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          {c.other?.role && <RoleChip role={c.other.role} />}
                          {c.unread > 0 && (
                            <span className="ml-auto bg-rose-500 text-white text-[0.55rem] rounded-full px-1.5"
                                  data-testid={`msg-unread-${c.id}`}>{c.unread}</span>
                          )}
                        </div>
                        <div className="text-[0.7rem] text-lifted-dim truncate mt-1">
                          {c.last_message_preview || "(no messages yet)"}
                        </div>
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </aside>

        {/* Conversation */}
        <div className="glass-panel min-h-[60vh] flex flex-col" data-testid="msg-thread">
          {conversationId ? (
            <ConversationView conversationId={conversationId} onAfterSend={load} />
          ) : (
            <div className="m-auto text-center p-8 text-lifted-dim">
              <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-60" />
              <div className="font-bebas tracking-[0.22em] text-sm">Select a conversation</div>
              <div className="text-[0.7rem] mt-1">or start a new one above.</div>
            </div>
          )}
        </div>

        {/* Phase 16 — Online Users sidebar */}
        <OnlineUsersPanel data={online} onMessage={startConvoWith} />
      </div>

      {composeOpen && (
        <ComposeModal onClose={() => setComposeOpen(false)}
                       onCreated={(cid) => { setComposeOpen(false); load(); navigate(`/messages/${cid}`); }} />
      )}
    </div>
  );
}

function OnlineUsersPanel({ data, onMessage }) {
  const groups = data?.groups || {};
  const roleKeys = Object.keys(groups).filter(
    (r) => (groups[r]?.online?.length || 0) + (groups[r]?.recent?.length || 0) > 0
  );
  return (
    <aside className="glass-panel overflow-hidden hidden lg:flex flex-col"
           data-testid="msg-online-panel">
      <div className="px-3 py-2 text-[0.65rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase border-b border-white/5 flex items-center gap-2">
        <Wifi className="h-3 w-3 text-lifted-green" />
        Active now · {(data?.online_total || 0)}
      </div>
      <div className="max-h-[70vh] overflow-y-auto">
        {!data ? (
          <div className="p-4 text-xs text-lifted-dim">Loading…</div>
        ) : roleKeys.length === 0 ? (
          <div className="p-4 text-xs text-lifted-dim">No one active right now.</div>
        ) : (
          roleKeys.map((role) => (
            <div key={role} className="px-3 py-2 border-b border-white/5"
                 data-testid={`msg-online-group-${role}`}>
              <div className="font-bebas tracking-[0.18em] text-[0.6rem] uppercase text-lifted-green mb-2">
                {ROLE_LABEL[role] || `Online ${role}s`}
              </div>
              <ul className="space-y-1.5">
                {[...(groups[role].online || []), ...(groups[role].recent || [])].map((u) => (
                  <li key={u.user_id} className="flex items-center gap-2">
                    <Avatar name={u.display_name} avatarUrl={u.avatar_url}
                            role={u.role} size={28}
                            online={u.online_status === "online"} />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-white truncate font-bebas tracking-[0.05em]">
                        {u.display_name}
                      </div>
                      <div className="text-[0.55rem] text-lifted-dim">
                        {u.online_status === "online" ? "Online now" : "Recently active"}
                      </div>
                    </div>
                    <button onClick={() => onMessage(u.user_id)}
                            className="text-[0.65rem] btn-lifted-ghost !py-1 !px-2"
                            data-testid={`msg-online-dm-${u.user_id}`}>
                      DM
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))
        )}
      </div>
    </aside>
  );
}

function ConversationView({ conversationId, onAfterSend }) {
  const [data, setData] = useState(null);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const { user } = useAuth();
  const endRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/messages/conversations/${conversationId}`);
      setData(d);
      // mark read
      await api.post(`/messages/conversations/${conversationId}/read`).catch(() => {});
    } catch (e) { setErr(apiErrorMessage(e)); }
  }, [conversationId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [data]);

  // Phase 17 — refresh on SSE new-message events targeted at this thread.
  useEffect(() => {
    const onNew = (e) => {
      const detail = e?.detail;
      // The notification carries the conversation_id when it's a DM.
      if (detail?.conversation_id === conversationId || detail?.type === "dm") {
        load();
      }
    };
    window.addEventListener("lifted:new-message", onNew);
    return () => window.removeEventListener("lifted:new-message", onNew);
  }, [conversationId, load]);

  const send = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      await api.post(`/messages/conversations/${conversationId}/messages`, { body: body.trim() });
      setBody("");
      await load();
      onAfterSend?.();
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setBusy(false); }
  };

  const report = async () => {
    const reason = window.prompt("Why are you reporting this conversation?");
    if (!reason) return;
    try {
      await api.post(`/messages/conversations/${conversationId}/report`, { reason });
      window.alert("Reported. Our team will review.");
    } catch (e) { setErr(apiErrorMessage(e)); }
  };

  if (err) return <div className="m-auto text-rose-300 p-6" data-testid="msg-thread-error">{err}</div>;
  if (!data) return <div className="m-auto p-6 text-lifted-dim text-sm">Loading…</div>;

  const me = user?.id;
  const other = (data.participants || []).find((p) => p.id !== me) || {};

  return (
    <>
      <header className="flex items-center gap-3 px-4 py-3 border-b border-white/5">
        <Link to="/messages" className="lg:hidden text-lifted-dim"><ArrowLeft className="h-4 w-4" /></Link>
        <Avatar name={other?.name} avatarUrl={other?.avatar_url} role={other?.role} size={36} />
        <div className="flex-1 min-w-0">
          <div className="font-bebas text-sm tracking-wide text-white truncate">{other?.name || "Unknown"}</div>
          {other?.role && <RoleChip role={other.role} />}
        </div>
        <button onClick={report} className="text-lifted-dim hover:text-rose-300 text-xs inline-flex items-center gap-1" data-testid="msg-report">
          <Flag className="h-3 w-3" /> Report
        </button>
      </header>

      <div className="flex-1 overflow-y-auto p-4 space-y-2 max-h-[55vh]" data-testid="msg-thread-list">
        {(() => {
          const me = user?.id;
          const otherId = ((data.participants || []).find((p) => p.id !== me) || {}).id;
          const otherReadAt = (data.read_markers || {})[otherId];
          // Find the last own message that was already read
          const own = (data.messages || []).filter((m) => m.sender_id === me);
          const lastReadOwnId = otherReadAt
            ? [...own].reverse().find((m) => m.created_at <= otherReadAt)?.id
            : null;
          return (data.messages || []).map((m) => {
            const mine = m.sender_id === me;
            const senderParticipant = (data.participants || []).find((p) => p.id === m.sender_id);
            const isLastReadOwn = mine && m.id === lastReadOwnId;
            return (
              <div key={m.id} className={`flex items-end gap-2 ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                {!mine && (
                  <Avatar name={senderParticipant?.name} avatarUrl={senderParticipant?.avatar_url}
                          role={senderParticipant?.role} size={24} />
                )}
                <div className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${
                  mine ? "bg-lifted-green/15 border border-lifted-green/30 text-lifted-text"
                       : "bg-white/5 border border-white/10 text-lifted-text"
                }`}>
                  <div className="whitespace-pre-wrap break-words">{m.body}</div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <div className="text-[0.55rem] text-lifted-dim font-mono">{(m.created_at || "").slice(11, 16)}</div>
                    {mine && (
                      <span
                        className={`text-[0.55rem] font-bebas tracking-[0.18em] uppercase ${isLastReadOwn ? "text-lifted-green" : "text-lifted-dim"}`}
                        data-testid={isLastReadOwn ? `msg-read-${m.id}` : `msg-sent-${m.id}`}
                      >
                        {isLastReadOwn ? "Read" : "Sent"}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          });
        })()}
        <div ref={endRef} />
      </div>

      <div className="border-t border-white/5 p-3 flex gap-2">
        <input value={body} onChange={(e) => setBody(e.target.value)}
               onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
               placeholder="Type a message…"
               className="flex-1 bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-lifted-green/40"
               data-testid="msg-input" />
        <button onClick={send} disabled={!body.trim() || busy}
                className="btn-lifted text-xs inline-flex items-center gap-1 disabled:opacity-50" data-testid="msg-send">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />} Send
        </button>
      </div>
    </>
  );
}

function ComposeModal({ onClose, onCreated }) {
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [results, setResults] = useState([]);
  const [initial, setInitial] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const search = useCallback(async () => {
    try {
      const { data } = await api.get("/messages/recipients/search", { params: { q, role } });
      setResults(data.users || []);
    } catch (e) { setErr(apiErrorMessage(e)); }
  }, [q, role]);
  useEffect(() => { search(); }, [search]);

  const start = async (uid) => {
    setBusy(true);
    try {
      const { data } = await api.post("/messages/conversations", {
        recipient_user_id: uid,
        initial_message: initial.trim() || undefined,
      });
      onCreated?.(data.conversation.id);
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 grid place-items-center p-4" onClick={onClose} data-testid="msg-compose-modal">
      <div className="glass-panel p-5 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="font-bebas tracking-[0.22em] text-sm">NEW CONVERSATION</div>
          <button onClick={onClose} className="text-lifted-dim"><X className="h-4 w-4" /></button>
        </div>
        <div className="flex gap-2 mb-2">
          <div className="flex-1 relative">
            <Search className="h-3 w-3 absolute left-2 top-1/2 -translate-y-1/2 text-lifted-dim" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email"
                   className="w-full bg-black/40 border border-white/10 rounded-md pl-7 pr-3 py-2 text-sm focus:outline-none focus:border-lifted-green/40"
                   data-testid="msg-search" />
          </div>
          <select value={role} onChange={(e) => setRole(e.target.value)}
                  className="bg-black/40 border border-white/10 rounded-md px-2 text-xs"
                  data-testid="msg-role-filter">
            <option value="">Any role</option>
            <option value="affiliate">Affiliate</option>
            <option value="driver">Driver</option>
            <option value="partner">Partner</option>
            <option value="franchisee">Franchise</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        <textarea value={initial} onChange={(e) => setInitial(e.target.value)} rows={2}
                  placeholder="Optional first message…"
                  className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-lifted-green/40 mb-2"
                  data-testid="msg-initial" />
        <div className="max-h-60 overflow-y-auto space-y-1" data-testid="msg-results">
          {err && <div className="text-rose-300 text-xs flex items-center gap-1"><AlertCircle className="h-3 w-3" /> {err}</div>}
          {results.length === 0 ? (
            <div className="text-xs text-lifted-dim p-2">No matches.</div>
          ) : results.map((u) => (
            <button key={u.id} onClick={() => start(u.id)} disabled={busy}
                    className="w-full text-left px-3 py-2 rounded-md hover:bg-white/5 flex items-center gap-2 disabled:opacity-50"
                    data-testid={`msg-pick-${u.id}`}>
              <Avatar name={u.name} avatarUrl={u.avatar_url} role={u.role} size={28}
                      online={u.online_status === "online"} />
              <div className="flex-1">
                <div className="text-sm">{u.name}</div>
              </div>
              <RoleChip role={u.role} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
